import { useLazyAsyncData } from '#imports'
import { type MaybeRefOrGetter, computed, toValue } from 'vue'

import {
  type PaymentMethod,
  PaymentMethodCode,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { PAYMENT_METHOD_CONFIGS } from '../config/methods'
import {
  type PaymentMethodAvailabilityOptions,
  PaymentMethodAvailabilityStatus,
} from '../config/methods.type'

const PAYMENT_METHOD_AVAILABILITY_TIMEOUT = 1000

export type PaymentMethodAvailabilities = Partial<
  Record<PaymentMethodCode, PaymentMethodAvailabilityStatus>
>

export function usePaymentMethodAvailabilityContext(
  paymentMethod: PaymentMethod,
  options?: PaymentMethodAvailabilityOptions,
) {
  const config = PAYMENT_METHOD_CONFIGS[paymentMethod.bmCode]

  return !!config && 'useAvailabilityContext' in config
    ? config.useAvailabilityContext(paymentMethod, options)
    : undefined
}

export async function checkPaymentMethodAvailability(
  paymentMethod: PaymentMethod,
  context: unknown,
): Promise<PaymentMethodAvailabilityStatus> {
  if (import.meta.server) {
    // It should never happen that we check the availability of a payment method on the server side.
    // But if it does, we consider the payment method as available.
    return PaymentMethodAvailabilityStatus.AVAILABLE
  }

  const getAvailabilityInBrowser =
    PAYMENT_METHOD_CONFIGS[paymentMethod.bmCode]?.getAvailabilityInBrowser ||
    (() =>
      Promise.resolve<PaymentMethodAvailabilityStatus>(
        PaymentMethodAvailabilityStatus.AVAILABLE,
      ))

  const availabilityCheck = getAvailabilityInBrowser(
    paymentMethod.config || {},
    context,
  ).catch((error: unknown): PaymentMethodAvailabilityStatus => {
    useLogger().error('[PAYMENT] Payment method availability check failed', {
      error,
      payment: {
        method: paymentMethod,
      },
      owners: ['bot-squad-payin-front'],
    })

    // Consider the payment method unavailable on failure.
    return PaymentMethodAvailabilityStatus.UNAVAILABLE
  })

  let didTimeout = false
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<PaymentMethodAvailabilityStatus>((resolve) => {
    timeoutId = setTimeout(() => {
      didTimeout = true
      resolve(
        PaymentMethodAvailabilityStatus.MAY_REQUIRE_ADDITIONAL_USER_ACTION,
      )
    }, PAYMENT_METHOD_AVAILABILITY_TIMEOUT)
  })

  // When a timeout occurs we want a log to inform us
  // that the user could have paid if we waited longer.
  void availabilityCheck.then((availability) => {
    if (
      didTimeout &&
      availability.isAvailable &&
      !availability.mayRequireAdditionalUserActionWithPaymentProvider
    ) {
      useLogger().info('[PAYMENT] user_could_have_pay', {
        payment: {
          event: { key: 'user_could_have_pay' },
          method: paymentMethod,
          paymentMethodCode: paymentMethod.bmCode,
        },
        owners: ['bot-squad-payin-front'],
      })
    }
  })

  try {
    return timeout
      ? await Promise.race([availabilityCheck, timeout])
      : await availabilityCheck
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * Get the availabilitiy for each payment methods, based on the current user setup.
 * It relies on `getAvailabilityInBrowser` from
 * `PAYMENT_METHOD_CONFIGS`, or consider it is always available if not defined.
 * Note that it will return the same list on server-side.
 */
async function getPaymentMethodAvailabilities(
  paymentMethods: PaymentMethod[],
  contexts: Record<PaymentMethodCode, unknown>,
): Promise<PaymentMethodAvailabilities> {
  const entries = await Promise.all(
    paymentMethods.map(async (method) => {
      const availability = await checkPaymentMethodAvailability(
        method,
        contexts[method.bmCode],
      )

      return [method.bmCode, availability] as const
    }),
  )

  return Object.fromEntries(entries)
}

/**
 * Get the payment methods available in the current browser context.
 * This will only resolve in the browser and keep pending on the server.
 */
export const useAvailablePaymentMethods = (
  paymentMethods: MaybeRefOrGetter<PaymentMethod[]>,
  options?: PaymentMethodAvailabilityOptions,
) => {
  const contexts = toValue(paymentMethods).reduce(
    (ctx, paymentMethod) => ({
      ...ctx,
      [paymentMethod.bmCode]: usePaymentMethodAvailabilityContext(
        paymentMethod,
        options,
      ),
    }),
    {} as Record<PaymentMethodCode, unknown>,
  )

  const asyncData = useLazyAsyncData<PaymentMethodAvailabilities>(
    () => getPaymentMethodAvailabilities(toValue(paymentMethods), contexts),
    {
      server: false,
    },
  )

  const availablePaymentMethods = computed(() => {
    const availabilities = asyncData.data.value
    if (!availabilities) {
      // FIXME: here for legacy reasons, as it allow to show one skeleton by payment method while we load,
      // but it's weird to return all payment methods,
      // when the availability is not yet resolved.
      return toValue(paymentMethods)
    }

    return toValue(paymentMethods).filter((paymentMethod) => {
      const availability = availabilities[paymentMethod.bmCode]

      return availability?.isAvailable === true
    })
  })

  return {
    ...asyncData,

    /**
     * Payment methods that are definitely or potentially available.
     * This is the list of payment methods that can be displayed to the user.
     */
    availablePaymentMethods,
    paymentMethodAvailabilities: asyncData.data,
  }
}
